import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { Capabilities, type Capability, type Me } from '@/api/client'
import { useLogout, useMe } from '@/features/auth/useAuth'
import { useOverlay } from '@/lib/useOverlay'
import { CloseIcon } from '@/components/FilterSheet'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { MadeBy } from '@/components/MadeBy'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'

type CommonKey = keyof typeof commonMessages.en

interface NavItem {
  to: string
  label: CommonKey
  /** Absent = admin-only (the audit trail is gated on IsAdmin, not a page capability). */
  capability?: Capability
  icon: ReactNode
}

interface NavGroup {
  id: string
  label: CommonKey
  items: NavItem[]
}

function pathMatches(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(`${to}/`)
}

function allowed(item: NavItem, me: Pick<Me, 'capabilities' | 'isAdmin'> | null | undefined) {
  return item.capability ? !!me?.capabilities.includes(item.capability) : !!me?.isAdmin
}

// Primary field tools stay as pills. Everything that landed with the pilot modules
// (workflow, billing, inventory, fleet, people, crews…) folds into dropdowns so the
// bar still fits on a laptop.
const FIELD: NavItem[] = [
  { to: '/dashboard', label: 'navDashboard', capability: Capabilities.ViewDashboard, icon: <Icon d="M4 13h6V4H4zM14 20h6v-9h-6zM4 20h6v-3H4zM14 7h6V4h-6z" /> },
  { to: '/rtk', label: 'navRtk', capability: Capabilities.ViewRtk, icon: <Icon d="M9 4 3 6.5v13.5l6-2.5 6 2.5 6-2.5V4l-6 2.5zM9 4v13.5M15 6.5V20" /> },
  { to: '/fleet', label: 'navFleet', capability: Capabilities.ViewFleet, icon: <Icon d="M3 13l2-5a2 2 0 0 1 1.9-1.4h10.2A2 2 0 0 1 19 8l2 5v4a1 1 0 0 1-1 1h-1M3 13v4a1 1 0 0 0 1 1h1M3 13h18M7.5 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M16.5 18.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M9 18h6" /> },
  { to: '/aufmass', label: 'navAufmass', capability: Capabilities.ViewAufmassGrid, icon: <Icon d="M4 5h16v14H4zM4 10h16M4 15h16M10 5v14" /> },
]

const GROUPS: NavGroup[] = [
  {
    id: 'work',
    label: 'navGroupWork',
    items: [
      { to: '/my-work', label: 'navMyWork', capability: Capabilities.ViewProjects, icon: <Icon d="M9 11l3 3 8-8M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9" /> },
      { to: '/portfolio', label: 'navExecution', capability: Capabilities.ViewProjects, icon: <Icon d="M3 12h4l3-8 4 16 3-8h4" /> },
      { to: '/projects', label: 'navProjects', capability: Capabilities.ViewProjects, icon: <Icon d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /> },
      { to: '/billing', label: 'navBilling', capability: Capabilities.ViewBilling, icon: <Icon d="M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h3" /> },
    ],
  },
  {
    id: 'ops',
    label: 'navGroupOps',
    items: [
      { to: '/people', label: 'navPeople', capability: Capabilities.ViewPeople, icon: <Icon d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM9 12l2 2 4-4" /> },
      { to: '/inventory', label: 'navInventory', capability: Capabilities.ViewInventory, icon: <Icon d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6M12 3v6M12 15v6M3 12h6M15 12h6" /> },
      { to: '/crews', label: 'navCrews', icon: <Icon d="M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6M5 20v-1a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v1M4.5 11a2.2 2.2 0 1 0 0-4.4M19.5 11a2.2 2.2 0 1 0 0-4.4" /> },
      { to: '/bonus', label: 'navBonus', capability: Capabilities.ViewBonus, icon: <Icon d="M12 3v18M16.5 7.5C16 5.9 14.3 5 12 5 9.2 5 7.5 6.3 7.5 8.2c0 4.3 9 2.4 9 7 0 2-1.9 3.3-4.5 3.3-2.5 0-4.3-1-4.8-2.8" /> },
    ],
  },
  {
    id: 'admin',
    label: 'navGroupAdmin',
    items: [
      { to: '/users', label: 'navUsers', capability: Capabilities.ManageUsers, icon: <Icon d="M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M21 19v-1a4 4 0 0 0-3-3.9M15.5 3.1a3.5 3.5 0 0 1 0 6.8" /> },
      { to: '/mobile-users', label: 'navMobileUsers', capability: Capabilities.ManageUsers, icon: <Icon d="M12 18h.01M8 21h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z" /> },
      { to: '/changelog', label: 'navChangelog', icon: <Icon d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2" /> },
    ],
  },
]

const desktopLink = ({ isActive }: { isActive: boolean }) =>
  [
    'rounded-full px-3 py-1.5 text-sm font-medium transition',
    isActive
      ? 'bg-brand-50 text-brand-700 dark:bg-brand-700/20 dark:text-brand-100'
      : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800',
  ].join(' ')

const drawerLink = ({ isActive }: { isActive: boolean }) =>
  [
    'flex min-h-12 items-center gap-3 rounded-xl px-3 text-[15px] font-medium transition',
    isActive
      ? 'bg-brand-600 text-white shadow-sm'
      : 'text-neutral-800 hover:bg-black/5 active:bg-black/10 dark:text-neutral-100 dark:hover:bg-white/10',
  ].join(' ')

export function AppShell() {
  const { data: me } = useMe()
  const logout = useLogout()
  const t = useT(commonMessages)
  const [menuOpen, setMenuOpen] = useState(false)
  const drawer = useRef<HTMLDivElement>(null)
  const header = useRef<HTMLElement>(null)

  // Aufmaß, RTK and the fleet map are full-bleed: the spreadsheet / map owns the viewport
  // and this bar floats over it. On every other page the bar is sticky and in flow, so
  // content starts beneath it.
  const { pathname } = useLocation()
  const fullBleed = pathname.startsWith('/aufmass') || pathname.startsWith('/rtk')
    || pathname === '/fleet' || pathname.startsWith('/fleet/map')
  // Projects is a wide, many-column data table — give it far more room than the reading-width
  // 6xl the other in-flow pages use.
  const wide = pathname.startsWith('/projects')

  const field = FIELD.filter((item) => allowed(item, me))
  const groups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter((item) => allowed(item, me)) }))
    .filter((g) => g.items.length > 0)
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const initials = (me?.name ?? '?')
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  useEffect(() => { setMenuOpen(false); setOpenGroup(null) }, [pathname])
  useOverlay(menuOpen, () => setMenuOpen(false), drawer)

  // Publish the bar's real height (safe-area padding included) as --nav-h. The full-bleed
  // screens place their grid / map chrome under it; a hard-coded inset was right on one
  // phone and wrong on the next, and badly wrong in landscape, where the bar is shorter.
  useLayoutEffect(() => {
    const el = header.current
    if (!el) return
    const publish = () => document.documentElement.style.setProperty('--nav-h', `${el.offsetHeight}px`)
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div className={fullBleed ? 'h-dvh overflow-hidden' : 'flex min-h-dvh flex-col overflow-x-hidden'}>
      <header
        ref={header}
        className={`z-40 flex justify-center px-3 print:hidden pt-[max(0.75rem,env(safe-area-inset-top))] md:px-4 md:pt-4 short:pt-[max(0.5rem,env(safe-area-inset-top))] ${
          // pointer-events-none so the grid stays usable either side of the centred pill.
          fullBleed ? 'pointer-events-none fixed inset-x-0 top-0' : 'sticky top-0'
        }`}
      >
        <nav
          aria-label="Main"
          className="pointer-events-auto flex w-full max-w-6xl items-center gap-1 rounded-2xl bg-white/80 py-1.5 pl-3 pr-1.5 shadow-sm ring-1 ring-black/5 backdrop-blur-xl backdrop-saturate-150 md:px-4 md:py-2 lg:pr-4 short:py-1 dark:bg-neutral-900/80 dark:ring-white/10"
        >
          <NavLink to="/" className="mr-1 flex items-center gap-2 font-semibold tracking-tight md:mr-4">
            <img src="/logo_transparent.png" alt="" aria-hidden className="h-7 w-7 object-contain short:h-6 short:w-6" />
            Aufmass
          </NavLink>

          <div className="hidden min-w-0 flex-1 items-center gap-0.5 lg:flex">
            {field.map((item) => (
              <NavLink key={item.to} to={item.to} className={desktopLink}>
                {t(item.label)}
              </NavLink>
            ))}
            {groups.map((group) =>
              group.items.length === 1 ? (
                <NavLink key={group.items[0]!.to} to={group.items[0]!.to} className={desktopLink}>
                  {t(group.items[0]!.label)}
                </NavLink>
              ) : (
                <NavDropdown
                  key={group.id}
                  label={t(group.label)}
                  open={openGroup === group.id}
                  active={group.items.some((item) => pathMatches(pathname, item.to))}
                  onToggle={() => setOpenGroup((cur) => (cur === group.id ? null : group.id))}
                  onClose={() => setOpenGroup(null)}
                  items={group.items.map((item) => ({
                    to: item.to,
                    label: t(item.label),
                    icon: item.icon,
                    active: pathMatches(pathname, item.to),
                  }))}
                />
              ),
            )}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden lg:block"><LanguageSwitcher /></div>
            <span
              title={me?.name ?? ''}
              className="hidden size-8 place-items-center rounded-full bg-neutral-200 text-xs font-semibold lg:grid dark:bg-neutral-700"
            >
              {initials}
            </span>
            <button
              onClick={() => logout.mutate()}
              className="hidden text-sm text-neutral-500 hover:text-neutral-900 lg:inline dark:hover:text-neutral-100"
            >
              {t('signOut')}
            </button>
            <button
              type="button"
              className="grid size-11 place-items-center rounded-xl text-neutral-700 transition hover:bg-black/5 active:bg-black/10 lg:hidden short:size-9 dark:text-neutral-200 dark:hover:bg-white/10"
              aria-label={t('openMenu')}
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </nav>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            className="overlay-fade absolute inset-0 cursor-default bg-black/40 backdrop-blur-[2px]"
            onClick={() => setMenuOpen(false)}
          />
          <div
            ref={drawer}
            className="drawer-in absolute inset-y-0 left-0 flex w-[min(20rem,86vw)] flex-col bg-white/90 shadow-2xl frost-solid ring-1 ring-black/5 backdrop-blur-2xl backdrop-saturate-150 dark:bg-neutral-900/90 dark:ring-white/10"
            style={{
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
              paddingLeft: 'env(safe-area-inset-left)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label={t('menu')}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <span className="flex items-center gap-2 font-semibold tracking-tight">
                <img src="/logo_transparent.png" alt="" aria-hidden className="h-7 w-7 object-contain" />
                Aufmass
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label={t('closeMenu')}
                className="grid size-11 place-items-center rounded-full text-neutral-500 transition hover:bg-black/5 dark:text-neutral-300 dark:hover:bg-white/10"
              >
                <CloseIcon />
              </button>
            </div>
            <nav aria-label={t('pages')} className="flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 py-2">
              {field.map((item) => (
                <NavLink key={item.to} to={item.to} replace className={drawerLink}>
                  <span className="shrink-0 opacity-90">{item.icon}</span>
                  {t(item.label)}
                </NavLink>
              ))}
              {groups.map((group) => (
                <div key={group.id} className="pt-3">
                  <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                    {t(group.label)}
                  </div>
                  {group.items.map((item) => (
                    <NavLink key={item.to} to={item.to} replace className={drawerLink}>
                      <span className="shrink-0 opacity-90">{item.icon}</span>
                      {t(item.label)}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <div className="border-t border-black/5 px-4 py-4 dark:border-white/10">
              <div className="mb-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{t('language')}</div>
                <LanguageSwitcher variant="list" />
              </div>
              <div className="flex items-center gap-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-50 text-sm font-semibold text-brand-700 dark:bg-brand-700/20 dark:text-brand-100">
                  {initials}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{me?.name}</div>
                  {me?.email && <div className="truncate text-xs text-neutral-500">{me.email}</div>}
                </div>
              </div>
              <button
                onClick={() => logout.mutate()}
                className="mt-3 h-11 w-full rounded-xl text-sm font-medium text-neutral-700 ring-1 ring-neutral-300 transition hover:bg-black/5 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-white/10"
              >
                {t('signOut')}
              </button>
            </div>
          </div>
        </div>
      )}

      <main className={fullBleed ? '' : `mx-auto w-full min-w-0 flex-1 px-4 py-6 ${wide ? 'max-w-[112rem]' : 'max-w-6xl'}`}>
        <Outlet />
      </main>

      {!fullBleed && (
        <footer className="mt-auto px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-8 text-center">
          <MadeBy className="print:hidden text-[11px] leading-snug text-neutral-400 dark:text-neutral-500" />
        </footer>
      )}
    </div>
  )
}

function NavDropdown({
  label,
  open,
  active,
  onToggle,
  onClose,
  items,
}: {
  label: string
  open: boolean
  active: boolean
  onToggle: () => void
  onClose: () => void
  items: { to: string; label: string; icon: ReactNode; active: boolean }[]
}) {
  const wrap = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (!open || !btn.current) return
    const place = () => {
      const r = btn.current!.getBoundingClientRect()
      const width = menu.current?.offsetWidth ?? 192
      const left = Math.min(r.left, Math.max(8, window.innerWidth - width - 8))
      setPos({ top: r.bottom + 6, left })
    }
    place()
    // Second pass once the menu is in the DOM so we can keep it on-screen.
    requestAnimationFrame(place)
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: PointerEvent) => {
      const t = e.target as Node
      if (wrap.current?.contains(t) || menu.current?.contains(t)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('pointerdown', onDoc)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDoc)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  return (
    <div ref={wrap} className="relative">
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
        className={[
          'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition',
          active || open
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-700/20 dark:text-brand-100'
            : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800',
        ].join(' ')}
      >
        {label}
        <svg viewBox="0 0 20 20" className={`h-3.5 w-3.5 opacity-70 transition ${open ? 'rotate-180' : ''}`} fill="currentColor" aria-hidden>
          <path d="M5.8 7.5 10 11.7l4.2-4.2 1.1 1.1L10 13.9 4.7 8.6z" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={menu}
          role="menu"
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-50 min-w-48 rounded-xl bg-white p-1 shadow-xl ring-1 ring-black/10 dark:bg-neutral-900 dark:ring-white/10"
        >
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              role="menuitem"
              className={[
                'flex min-h-10 items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium transition',
                item.active
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-700/20 dark:text-brand-100'
                  : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800',
              ].join(' ')}
            >
              <span className="shrink-0 opacity-80">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

function Icon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  )
}
